import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiBell, FiCheck, FiCheckCircle, FiTrash2, FiFilter, FiSend, FiX, FiAlertTriangle, FiInfo, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ title: '', message: '', type: 'info', priority: 'medium' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await notificationAPI.getAll({ limit: 100 });
      setNotifications(res.data.notifications || res.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const markAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success('All marked as read');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.delete(id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      toast.success('Notification deleted');
    } catch { toast.error('Failed'); }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    try {
      await notificationAPI.broadcast(broadcastForm);
      toast.success('Broadcast sent to all users');
      setShowBroadcast(false);
      setBroadcastForm({ title: '', message: '', type: 'info', priority: 'medium' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.isRead;
    if (filter === 'read') return n.isRead;
    if (filter !== 'all') return n.type === filter;
    return true;
  });

  const typeIcon = (type) => {
    switch (type) {
      case 'warning': return <FiAlertTriangle size={18} color="#f59e0b" />;
      case 'error': case 'critical': return <FiAlertCircle size={18} color="#ef4444" />;
      case 'success': return <FiCheckCircle size={18} color="#10b981" />;
      default: return <FiInfo size={18} color="#3b82f6" />;
    }
  };

  const priorityColor = (p) => {
    switch (p) {
      case 'high': case 'critical': return { bg: '#fee2e2', color: '#991b1b' };
      case 'medium': return { bg: '#fef3c7', color: '#854d0e' };
      default: return { bg: '#f1f5f9', color: '#475569' };
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Notifications</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>{unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {unreadCount > 0 && (
            <button onClick={markAllRead} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontSize: 13, color: '#3b82f6' }}>
              <FiCheck size={14} /> Mark all read
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowBroadcast(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              <FiSend size={14} /> Broadcast
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { value: 'all', label: 'All' },
          { value: 'unread', label: `Unread (${unreadCount})` },
          { value: 'info', label: 'Info' },
          { value: 'warning', label: 'Warnings' },
          { value: 'error', label: 'Errors' },
          { value: 'success', label: 'Success' },
        ].map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            style={{ padding: '6px 16px', borderRadius: 20, border: 'none', fontWeight: 500, fontSize: 13, cursor: 'pointer', background: filter === f.value ? '#3b82f6' : '#f1f5f9', color: filter === f.value ? '#fff' : '#64748b', transition: 'all 0.2s' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Notification List */}
      <div style={{ display: 'grid', gap: 8 }}>
        {filtered.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 16, padding: 60, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <FiBell size={48} style={{ color: '#e2e8f0', marginBottom: 16 }} />
            <p style={{ fontSize: 16, fontWeight: 500, color: '#94a3b8' }}>No notifications</p>
          </div>
        ) : filtered.map(n => {
          const pc = priorityColor(n.priority);
          return (
            <div key={n._id}
              onClick={() => { markAsRead(n._id); if (n.link) navigate(n.link); }}
              style={{
                background: n.isRead ? '#fff' : '#f8faff', borderRadius: 12,
                padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                border: n.isRead ? '1px solid #f1f5f9' : '1px solid #bfdbfe',
                cursor: n.link ? 'pointer' : 'default', transition: 'all 0.2s',
                display: 'flex', alignItems: 'flex-start', gap: 14,
              }}>
              <div style={{ marginTop: 2 }}>{typeIcon(n.type)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: n.isRead ? 500 : 700, color: '#0f172a' }}>{n.title}</span>
                  {!n.isRead && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />}
                  {n.priority && n.priority !== 'low' && (
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: pc.bg, color: pc.color }}>
                      {n.priority}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: 0 }}>{n.message}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(n.createdAt).toLocaleString()}</span>
                  {n.category && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#64748b' }}>{n.category}</span>}
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(n._id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, flexShrink: 0 }}>
                <FiTrash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Broadcast Modal */}
      {showBroadcast && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 500 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>Broadcast Notification</h2>
              <button onClick={() => setShowBroadcast(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <form onSubmit={handleBroadcast}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Title</label>
                <input value={broadcastForm.title} onChange={e => setBroadcastForm({ ...broadcastForm, title: e.target.value })} required placeholder="Notification title" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Message</label>
                <textarea value={broadcastForm.message} onChange={e => setBroadcastForm({ ...broadcastForm, message: e.target.value })} required rows={3} placeholder="Enter message..." style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select value={broadcastForm.type} onChange={e => setBroadcastForm({ ...broadcastForm, type: e.target.value })} style={inputStyle}>
                    {['info', 'warning', 'error', 'success'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select value={broadcastForm.priority} onChange={e => setBroadcastForm({ ...broadcastForm, priority: e.target.value })} style={inputStyle}>
                    {['low', 'medium', 'high', 'critical'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" style={{ width: '100%', padding: 12, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
                Send to All Users
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default Notifications;
